/** Hooks TanStack Query pour les pointages et résumés. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeLogsApi } from '../services/api';

const API_URL = import.meta.env.VITE_API_URL;

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export interface TimeLogPayload {
  employee_id?: number;
  reader_id?: number;
  type?: 'IN' | 'OUT';
  timestamp?: string;
}

export interface IncompleteDay {
  employee_id: number;
  employee_name: string;
  site_name: string;
  date: string;
  issue: 'missing_in' | 'missing_out';
  in_time: string | null;
  out_time: string | null;
}

// --- Lecture ---

export function useTimeLogs(params: {
  page?: number;
  per_page?: number;
  employee_id?: number;
  date_from?: string;
  date_to?: string;
  type?: string;
  reader_id?: number;
  activity?: string;
}) {
  return useQuery({
    queryKey: ['time-logs', params],
    queryFn: () => timeLogsApi.list(params),
    placeholderData: (prev) => prev,
  });
}

export function useEmployeeSummary(
  employee_id: number,
  date_from: string,
  date_to: string
) {
  return useQuery({
    queryKey: ['employee-summary', employee_id, date_from, date_to],
    queryFn: () => timeLogsApi.summary(employee_id, date_from, date_to),
    enabled: !!employee_id && !!date_from && !!date_to,
  });
}

// --- Pointages incomplets ---

export function useIncompleteLogs(days = 7) {
  return useQuery({
    queryKey: ['incomplete-logs', days],
    queryFn: async (): Promise<IncompleteDay[]> => {
      const res = await fetch(`${API_URL}/api/time-logs/incomplete?days=${days}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Erreur récupération incomplets');
      return res.json();
    },
  });
}

// --- CRUD ---

export function useCreateTimeLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Required<TimeLogPayload>) => {
      const res = await fetch(`${API_URL}/api/time-logs`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      return res.json();
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateTimeLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: TimeLogPayload }) => {
      const res = await fetch(`${API_URL}/api/time-logs/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      return res.json();
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteTimeLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_URL}/api/time-logs/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Erreur suppression');
    },
    onSuccess: () => invalidateAll(qc),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['time-logs'] });
  qc.invalidateQueries({ queryKey: ['employee-summary'] });
  qc.invalidateQueries({ queryKey: ['incomplete-logs'] });
  qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
  qc.invalidateQueries({ queryKey: ['dashboard-presence'] });
}
